import { useEffect, useState } from "react";
import { Box, Chip, Divider, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import Button from "@mui/material/Button";
import { translate } from "../../../../../translate/translate";
import NewNpiDialogComponent from "./new-npi-dialog.component";
import NpiDashboardBoardComponent from "./npi-dashboard-board.component";
import { BaseService } from "../../../../../services/base-service";
import { URLS } from "../../../../../services/app-urls";
import { ProjectType } from "../../../../../models/basic/template";
import { Client } from "../../../../../models/basic/client";
import { outlinedFieldHover, selectedChip, unselectedChip } from "../styles/npi-dashboard.styles";

const FILTERS_STORAGE_KEY = "npi_dashboard_filters";

type StoredFilters = {
  clientId: number | "";
  year: number | "";
  projectTypeId: number | null;
};

const readStoredFilters = (): StoredFilters | null => {
  try {
    const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeStoredFilters = (filters: StoredFilters) => {
  try {
    sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
  }
};

const NpiDashboardComponent = () => {
  const storedFilters = readStoredFilters();

  const [clients, setClients] = useState<Client[]>([]);
  const [years, setYears] = useState<number[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | "">(storedFilters?.clientId ?? "");
  const [selectedYear, setSelectedYear] = useState<number | "">(storedFilters?.year ?? "");
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState<number>(0);
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const [selectedProjectTypeId, setSelectedProjectTypeId] = useState<number | null>(
    storedFilters?.projectTypeId ?? null,
  );

  useEffect(() => {
    writeStoredFilters({
      clientId: selectedClientId,
      year: selectedYear,
      projectTypeId: selectedProjectTypeId,
    });
  }, [selectedClientId, selectedYear, selectedProjectTypeId]);

  const fetchProjectTypesInUse = () => {
    const npiService = new BaseService<any>(URLS.NPI);
    npiService.getAllFromListRoute("project-types-in-use").then((response: any) => {
      setProjectTypes(response.data);
      setSelectedProjectTypeId((current) =>
        response.data.some((item: ProjectType) => item.id === current) ? current : (response.data[0]?.id ?? null),
      );
    });
  };

  const fetchYearsInUse = () => {
    const npiService = new BaseService<any>(URLS.NPI);
    npiService.getAllFromListRoute("years-in-use").then((response: any) => {
      setYears(response.data);
      setSelectedYear((current) => (response.data.includes(current) ? current : (response.data[0] ?? "")));
    });
  };

  const fetchClients = () => {
    const clientService = new BaseService<Client>(URLS.CLIENT);
    clientService.clearParameters();
    clientService.addParameter("active", "true");
    clientService.getAll().then((response) => {
      setClients(response.data);
      setSelectedClientId((current) =>
        response.data.some((client: Client) => client.id === current) ? current : (response.data[0]?.id ?? ""),
      );
    });
  };

  useEffect(() => {
    fetchClients();
    fetchYearsInUse();
    fetchProjectTypesInUse();
  }, []);

  const handleNpiCreated = () => {
    fetchProjectTypesInUse();
    fetchYearsInUse();
    setDashboardRefreshKey((current) => current + 1);
  };

  return (
    <section className="npi-dashboard-page">
      <div
        className="floating-title"
        style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h5" sx={{ fontWeight: "700 !important", fontSize: "20px", letterSpacing: "0.15px", color: "#45485F" }}>
          {translate.t("dashboard")}
        </Typography>
        <Grid container spacing={2} alignItems="center" width="auto">
          <Grid>
            <FormControl size="small" sx={{ width: 220, ...outlinedFieldHover("4px") }}>
              <InputLabel id="dashboard-client-label" shrink>{translate.t("client")}</InputLabel>
              <Select
                labelId="dashboard-client-label"
                label={translate.t("client")}
                notched
                displayEmpty
                sx={{ fontSize: "14px" }}
                value={selectedClientId}
                onChange={(event) => setSelectedClientId(event.target.value as number)}>
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid>
            <FormControl size="small" sx={{ minWidth: 100, ...outlinedFieldHover("4px") }}>
              <InputLabel id="dashboard-year-label" shrink>{translate.t("year")}</InputLabel>
              <Select
                labelId="dashboard-year-label"
                label={translate.t("year")}
                notched
                displayEmpty
                sx={{ fontSize: "14px" }}
                value={selectedYear}
                onChange={(event) => setSelectedYear(event.target.value as number)}>
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpenDialog(true)}
              sx={{ backgroundColor: "#003FFB", "&:hover": { backgroundColor: "#003FFB", opacity: 0.9 } }}>
              {translate.t("new_npi").toUpperCase()}
            </Button>
          </Grid>
        </Grid>
      </div>
      <Box
        sx={{
          backgroundColor: "#fff",
          ml: "-15px",
          width: "calc(100% + 15px)",
          px: "15px",
          pt: 2,
          pb: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}>
        <Grid container alignItems="center" spacing={1} sx={{ mb: 2 }}>
          {projectTypes.map((projectType) => (
            <Grid key={projectType.id}>
              <Chip
                label={projectType.name}
                onClick={() => setSelectedProjectTypeId(projectType.id!)}
                sx={{
                  ...(selectedProjectTypeId === projectType.id ? selectedChip : unselectedChip),
                  fontSize: "16px",
                }}
              />
            </Grid>
          ))}
        </Grid>
        <Divider sx={{ mx: "-15px", width: "calc(100% + 30px)", borderBottom: "2px solid #E2E8F0" }} />
      </Box>
      <NpiDashboardBoardComponent
        clientId={selectedClientId || null}
        year={selectedYear || null}
        projectTypeId={selectedProjectTypeId}
        refreshKey={dashboardRefreshKey}
        clientName={clients.find((client) => client.id === selectedClientId)?.name ?? ""}
        projectTypeName={projectTypes.find((projectType) => projectType.id === selectedProjectTypeId)?.name ?? ""}
      />
      <NewNpiDialogComponent open={openDialog} onClose={() => setOpenDialog(false)} onCreated={handleNpiCreated} />
    </section>
  );
};

export default NpiDashboardComponent;
